/* ==========================================================================
   Brand interactions — no dependencies.
   Custom elements auto-upgrade when Shopify re-renders a section in the
   theme editor, so nothing needs manual re-initialising.
   ========================================================================== */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

/* --------------------------------------------------------------------------
   <sn-rail> — horizontal scroller with arrows and a progress bar
   -------------------------------------------------------------------------- */
class SnRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-rail-track]');
    if (!this.track) return;

    this.prev = this.querySelector('[data-rail-prev]');
    this.next = this.querySelector('[data-rail-next]');
    this.bar = this.querySelector('[data-rail-bar]');

    this.onScroll = this.onScroll.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.prev?.addEventListener('click', () => this.page(-1));
    this.next?.addEventListener('click', () => this.page(1));

    this.ro = new ResizeObserver(() => this.onScroll());
    this.ro.observe(this.track);

    this.onScroll();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.ro?.disconnect();
  }

  get step() {
    const first = this.track.firstElementChild;
    if (!first) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    // advance by whole cards, at least one, filling most of the viewport
    const card = first.getBoundingClientRect().width + gap;
    return Math.max(card, Math.floor(this.track.clientWidth / card) * card);
  }

  page(dir) {
    this.track.scrollBy({
      left: this.step * dir,
      behavior: REDUCED.matches ? 'auto' : 'smooth',
    });
  }

  onScroll() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const max = scrollWidth - clientWidth;
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft >= max - 1;

    if (this.prev) this.prev.disabled = atStart;
    if (this.next) this.next.disabled = atEnd || max <= 0;

    if (this.bar) {
      const visible = max > 0 ? clientWidth / scrollWidth : 1;
      const progress = max > 0 ? scrollLeft / max : 0;
      this.bar.style.width = `${Math.min(visible, 1) * 100}%`;
      this.bar.style.transform = `translateX(${(progress * (1 / Math.min(visible, 1) - 1)) * 100}%)`;
    }
  }
}
customElements.define('sn-rail', SnRail);

/* --------------------------------------------------------------------------
   <sn-molecule> — interactive 3D ingredient diagram on a 2D canvas.
   Nodes are placed on a sphere with a Fibonacci lattice, rotated in 3D,
   projected with a simple perspective divide and painted back-to-front.
   -------------------------------------------------------------------------- */
class SnMolecule extends HTMLElement {
  connectedCallback() {
    this.canvas = this.querySelector('canvas');
    if (!this.canvas) return;
    this.ctx = this.canvas.getContext('2d');

    this.nodes = this.readNodes();
    if (!this.nodes.length) return;

    this.rotX = -0.35;
    this.rotY = 0.6;
    this.velY = 0.0016;
    this.velX = 0;
    this.dragging = false;
    this.hover = -1;
    this.pinned = -1;

    this.coreColor = this.dataset.core || '#123A2E';
    this.inkColor = this.dataset.ink || '#0F1512';

    this.layout();
    this.bindPointer();
    this.bindList();

    this.resizeObserver = new ResizeObserver(() => this.layout());
    this.resizeObserver.observe(this.canvas);

    this.io = new IntersectionObserver((entries) => {
      this.visible = entries[0].isIntersecting;
      if (this.visible) this.start();
      else this.stop();
    }, { threshold: 0 });
    this.io.observe(this);
  }

  disconnectedCallback() {
    this.stop();
    this.resizeObserver?.disconnect();
    this.io?.disconnect();
  }

  readNodes() {
    // the ingredient rows sit in the sibling column, so scope the lookup to
    // this section's wrapper rather than to the element itself
    const scope = this.closest('.sn-mol') || document;
    return [...this.querySelectorAll('[data-node]')].map((el, i) => ({
      i,
      name: el.dataset.node,
      amount: el.dataset.amount || '',
      color: el.dataset.color || '#E4572E',
      row: scope.querySelector(`[data-row="${i}"]`),
    }));
  }

  /* Fibonacci sphere — even distribution regardless of node count */
  positions() {
    const n = this.nodes.length;
    const golden = Math.PI * (3 - Math.sqrt(5));
    return this.nodes.map((_, i) => {
      const y = n === 1 ? 0 : 1 - (i / (n - 1)) * 2;
      const r = Math.sqrt(Math.max(0, 1 - y * y));
      const theta = golden * i;
      return { x: Math.cos(theta) * r, y, z: Math.sin(theta) * r };
    });
  }

  layout() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = this.canvas.getBoundingClientRect();
    if (!rect.width) return;
    this.canvas.width = Math.round(rect.width * dpr);
    this.canvas.height = Math.round(rect.height * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.w = rect.width;
    this.h = rect.height;
    this.radius = Math.min(this.w, this.h) * 0.37;
    this.basePos = this.positions();
    this.draw();
  }

  bindPointer() {
    const stage = this.canvas.parentElement;

    const down = (e) => {
      this.dragging = true;
      this.lastX = e.clientX;
      this.lastY = e.clientY;
      stage.classList.add('is-touched');
      this.canvas.setPointerCapture?.(e.pointerId);
    };
    const move = (e) => {
      const rect = this.canvas.getBoundingClientRect();
      const inside =
        e.clientX >= rect.left && e.clientX <= rect.right &&
        e.clientY >= rect.top && e.clientY <= rect.bottom;
      this.pointer = inside ? { x: e.clientX - rect.left, y: e.clientY - rect.top } : null;

      if (this.dragging) {
        const dx = e.clientX - this.lastX;
        const dy = e.clientY - this.lastY;
        this.lastX = e.clientX;
        this.lastY = e.clientY;
        this.rotY += dx * 0.008;
        this.rotX += dy * 0.008;
        this.rotX = Math.max(-1.2, Math.min(1.2, this.rotX));
        this.velY = dx * 0.0006;
        if (!this.raf) this.draw();
      } else if (inside) {
        this.pickHover();
      }
    };
    const up = () => { this.dragging = false; };

    this.canvas.addEventListener('pointerdown', down);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    this.canvas.addEventListener('pointerleave', () => {
      this.pointer = null;
      if (!this.hoverFromList) this.setHover(-1);
    });

    this._teardown = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }

  bindList() {
    this.nodes.forEach((n) => {
      if (!n.row) return;
      n.row.addEventListener('mouseenter', () => this.setHover(n.i, true));
      n.row.addEventListener('mouseleave', () => this.setHover(-1, true));
      n.row.addEventListener('focusin', () => this.setHover(n.i, true));
      n.row.addEventListener('focusout', () => this.setHover(-1, true));
    });
  }

  pickHover() {
    if (!this.pointer || !this.projected) return;
    let found = -1;
    let best = Infinity;
    this.projected.forEach((p) => {
      const d = Math.hypot(p.sx - this.pointer.x, p.sy - this.pointer.y);
      if (d < p.r + 10 && d < best) { best = d; found = p.i; }
    });
    this.setHover(found);
  }

  setHover(i, fromList = false) {
    this.hoverFromList = fromList && i >= 0;
    if (this.hover === i) return;
    this.hover = i;
    this.nodes.forEach((n) => n.row?.classList.toggle('is-active', n.i === i));
    if (!fromList) this.canvas.style.cursor = i >= 0 ? 'pointer' : '';
    if (!this.raf) this.draw();
  }

  project() {
    const cosX = Math.cos(this.rotX), sinX = Math.sin(this.rotX);
    const cosY = Math.cos(this.rotY), sinY = Math.sin(this.rotY);
    const cx = this.w / 2, cy = this.h / 2;
    const depth = this.radius * 3.2;

    this.projected = this.basePos.map((p, i) => {
      // rotate Y then X
      let x = p.x * cosY - p.z * sinY;
      let z = p.x * sinY + p.z * cosY;
      let y = p.y * cosX - z * sinX;
      z = p.y * sinX + z * cosX;

      const scale = depth / (depth + z * this.radius);
      return {
        i,
        sx: cx + x * this.radius * scale,
        sy: cy + y * this.radius * scale,
        z,
        scale,
        r: (this.hover === i ? 13 : 9.5) * scale,
      };
    }).sort((a, b) => a.z - b.z);
  }

  draw() {
    const ctx = this.ctx;
    if (!ctx || !this.w) return;
    ctx.clearRect(0, 0, this.w, this.h);
    this.project();

    const cx = this.w / 2, cy = this.h / 2;

    // orbit ring — faint depth cue
    ctx.save();
    ctx.translate(cx, cy);
    ctx.scale(1, Math.abs(Math.sin(this.rotX)) * 0.85 + 0.14);
    ctx.beginPath();
    ctx.arc(0, 0, this.radius, 0, Math.PI * 2);
    ctx.strokeStyle = this.hexA(this.inkColor, 0.13);
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(this.rotY * 0.5);
    ctx.scale(Math.abs(Math.cos(this.rotX)) * 0.7 + 0.2, 1);
    ctx.beginPath();
    ctx.arc(0, 0, this.radius * 0.82, 0, Math.PI * 2);
    ctx.strokeStyle = this.hexA(this.inkColor, 0.07);
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();

    // bonds
    this.projected.forEach((p) => {
      const node = this.nodes[p.i];
      const active = this.hover === p.i;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(p.sx, p.sy);
      ctx.strokeStyle = active
        ? this.hexA(node.color, 0.85)
        : this.hexA(this.inkColor, 0.14 + (p.z + 1) * 0.09);
      ctx.lineWidth = active ? 2 : 1.25;
      ctx.stroke();
    });

    // core
    const coreR = this.radius * 0.2;
    const grad = ctx.createRadialGradient(
      cx - coreR * 0.4, cy - coreR * 0.5, coreR * 0.1, cx, cy, coreR
    );
    grad.addColorStop(0, this.hexA(this.coreColor, 0.95));
    grad.addColorStop(1, this.hexA(this.coreColor, 0.72));
    ctx.beginPath();
    ctx.arc(cx, cy, coreR, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.fill();

    // nodes, painted back to front
    this.projected.forEach((p) => {
      const node = this.nodes[p.i];
      const active = this.hover === p.i;
      const dim = 0.35 + ((p.z + 1) / 2) * 0.65;

      if (active) {
        ctx.beginPath();
        ctx.arc(p.sx, p.sy, p.r + 7 * p.scale, 0, Math.PI * 2);
        ctx.fillStyle = this.hexA(node.color, 0.16);
        ctx.fill();
      }

      const g = ctx.createRadialGradient(
        p.sx - p.r * 0.35, p.sy - p.r * 0.4, p.r * 0.1, p.sx, p.sy, p.r
      );
      g.addColorStop(0, this.hexA(node.color, active ? 1 : Math.min(dim + 0.25, 1)));
      g.addColorStop(1, this.hexA(node.color, active ? 0.92 : dim * 0.82));
      ctx.beginPath();
      ctx.arc(p.sx, p.sy, p.r, 0, Math.PI * 2);
      ctx.fillStyle = g;
      ctx.fill();

      if (active) {
        ctx.beginPath();
        ctx.arc(p.sx, p.sy, p.r, 0, Math.PI * 2);
        ctx.strokeStyle = this.hexA('#ffffff', 0.85);
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
    });

    // label for the hovered node
    if (this.hover >= 0) {
      const p = this.projected.find((q) => q.i === this.hover);
      const node = this.nodes[this.hover];
      if (p) this.label(node, p);
    }
  }

  label(node, p) {
    const ctx = this.ctx;
    const text = node.amount ? `${node.name}  ·  ${node.amount}` : node.name;
    ctx.font = '600 12px ui-sans-serif, system-ui, sans-serif';
    const w = ctx.measureText(text).width + 20;
    const h = 26;
    let x = p.sx + 14;
    let y = p.sy - h / 2;
    if (x + w > this.w - 4) x = p.sx - 14 - w;
    y = Math.max(4, Math.min(this.h - h - 4, y));

    ctx.fillStyle = this.inkColor;
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = '#ffffff';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, x + 10, y + h / 2 + 0.5);
  }

  hexA(hex, alpha) {
    const c = hex.replace('#', '');
    const full = c.length === 3 ? c.split('').map((s) => s + s).join('') : c;
    const n = parseInt(full, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }

  start() {
    if (this.raf || REDUCED.matches) { this.draw(); return; }
    const tick = () => {
      if (!this.dragging) {
        this.rotY += this.velY;
        this.velY += (0.0016 - this.velY) * 0.02; // ease back to idle drift
      }
      this.draw();
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  stop() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
  }
}
customElements.define('sn-molecule', SnMolecule);

/* --------------------------------------------------------------------------
   <sn-count> — number that counts up once, when scrolled into view
   -------------------------------------------------------------------------- */
class SnCount extends HTMLElement {
  connectedCallback() {
    const target = parseFloat(this.dataset.to);
    if (isNaN(target)) return;
    const prefix = this.dataset.prefix || '';
    const suffix = this.dataset.suffix || '';
    const decimals = parseInt(this.dataset.decimals || '0', 10);

    if (REDUCED.matches) {
      this.textContent = prefix + target.toFixed(decimals) + suffix;
      return;
    }

    const io = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      io.disconnect();
      const dur = 1400;
      const t0 = performance.now();
      const step = (now) => {
        const p = Math.min((now - t0) / dur, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        this.textContent = prefix + (target * eased).toFixed(decimals) + suffix;
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, { threshold: 0.4 });
    io.observe(this);
  }
}
customElements.define('sn-count', SnCount);

/* --------------------------------------------------------------------------
   <sn-variant-card> — inline dose pills on a product card.
   Swaps the displayed price and the variant the add-to-cart form submits, so
   a 10mg / 20mg choice never needs a trip to the product page.
   -------------------------------------------------------------------------- */
class SnVariantCard extends HTMLElement {
  connectedCallback() {
    this.pills = [...this.querySelectorAll('[data-dose]')];
    if (!this.pills.length) return;

    this.input = this.querySelector('[data-variant-input]');
    this.priceValue = this.querySelector('[data-price-value]');
    this.compare = this.querySelector('[data-compare-target]');
    this.addButton = this.querySelector('[data-add-button]');
    this.saveFlag = this.querySelector('[data-card-save]');

    this.pills.forEach((pill) => {
      pill.addEventListener('click', () => this.select(pill));
    });
  }

  select(pill) {
    const soldOut = pill.dataset.soldout === 'true';

    this.pills.forEach((p) => {
      const on = p === pill;
      p.classList.toggle('is-active', on);
      p.setAttribute('aria-pressed', String(on));
    });

    if (this.input) this.input.value = pill.dataset.variantId;
    if (this.priceValue) this.priceValue.textContent = pill.dataset.price;

    if (this.compare) {
      const cmp = pill.dataset.compare;
      this.compare.textContent = cmp || '';
      this.compare.hidden = !cmp;
    }
    // the badge is priced against the first variant, so it stops being true
    // as soon as another dose is chosen
    if (this.saveFlag) this.saveFlag.hidden = !pill.dataset.compare;

    if (this.addButton) {
      this.addButton.disabled = soldOut;
      const text = this.addButton.querySelector('span');
      if (text) text.textContent = soldOut ? 'Sold out' : 'Add';
    }
  }
}
customElements.define('sn-variant-card', SnVariantCard);

/* --------------------------------------------------------------------------
   <sn-helix> — interactive double helix.
   Two phase-offset sine strands sampled along t, rotated about Y, projected
   with the same perspective divide as the molecule and painted back to front.
   -------------------------------------------------------------------------- */
class SnHelix extends HTMLElement {
  connectedCallback() {
    this.canvas = this.querySelector('canvas');
    if (!this.canvas) return;
    this.ctx = this.canvas.getContext('2d');

    this.strandA = this.dataset.strandA || '#123A2E';
    this.strandB = this.dataset.strandB || '#E4572E';
    this.rungColor = this.dataset.rung || '#0F1512';
    this.turns = parseFloat(this.dataset.turns) || 3;
    this.samples = 132;
    this.rungEvery = 6;
    this.depthFactor = 4.2;

    this.rotY = 0.4;
    this.velY = 0.0035;
    this.dragging = false;
    this.hover = -1;

    this.markers = this.readMarkers();

    this.layout();
    this.bindPointer();
    this.bindList();

    this.ro = new ResizeObserver(() => this.layout());
    this.ro.observe(this.canvas);

    this.io = new IntersectionObserver((e) => {
      if (e[0].isIntersecting) this.start();
      else this.stop();
    }, { threshold: 0 });
    this.io.observe(this);
  }

  disconnectedCallback() {
    this.stop();
    this.ro?.disconnect();
    this.io?.disconnect();
  }

  readMarkers() {
    const scope = this.closest('.sn-helix') || document;
    return [...this.querySelectorAll('[data-marker]')].map((el, i) => ({
      i,
      label: el.dataset.marker,
      at: Math.min(Math.max(parseFloat(el.dataset.at) || 50, 0), 100) / 100,
      color: el.dataset.color || this.strandB,
      row: scope.querySelector(`[data-hrow="${i}"]`),
    }));
  }

  layout() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = this.canvas.getBoundingClientRect();
    if (!rect.width) return;
    this.canvas.width = Math.round(rect.width * dpr);
    this.canvas.height = Math.round(rect.height * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.w = rect.width;
    this.h = rect.height;
    this.bg = this.resolveBg();
    this.radius = Math.min(this.w * 0.34, this.h * 0.2);
    const maxScale = this.depthFactor / (this.depthFactor - 1);
    this.span = (this.h * 0.88) / maxScale;
    this.draw();
  }

  /* Depth is expressed by mixing toward the surface behind the canvas rather
     than by alpha — overlapping translucent segments double-blend and speckle. */
  resolveBg() {
    let el = this;
    while (el && el !== document.documentElement) {
      const c = getComputedStyle(el).backgroundColor;
      const m = c.match(/rgba?\(([^)]+)\)/);
      if (m) {
        const parts = m[1].split(',').map((n) => parseFloat(n));
        if (parts.length < 4 || parts[3] > 0) return { r: parts[0], g: parts[1], b: parts[2] };
      }
      el = el.parentElement;
    }
    return { r: 255, g: 255, b: 255 };
  }

  mix(hex, amount) {
    const c = hex.replace('#', '');
    const full = c.length === 3 ? c.split('').map((x) => x + x).join('') : c;
    const n = parseInt(full, 16);
    const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    const t = Math.max(0, Math.min(1, amount));
    const bg = this.bg || { r: 255, g: 255, b: 255 };
    return `rgb(${Math.round(bg.r + (r - bg.r) * t)}, ${Math.round(bg.g + (g - bg.g) * t)}, ${Math.round(bg.b + (b - bg.b) * t)})`;
  }

  bindPointer() {
    const stage = this.canvas.parentElement;
    const down = (e) => {
      this.dragging = true;
      this.lastX = e.clientX;
      stage.classList.add('is-touched');
      this.canvas.setPointerCapture?.(e.pointerId);
    };
    const move = (e) => {
      const rect = this.canvas.getBoundingClientRect();
      const inside =
        e.clientX >= rect.left && e.clientX <= rect.right &&
        e.clientY >= rect.top && e.clientY <= rect.bottom;
      this.pointer = inside ? { x: e.clientX - rect.left, y: e.clientY - rect.top } : null;

      if (this.dragging) {
        const dx = e.clientX - this.lastX;
        this.lastX = e.clientX;
        this.rotY += dx * 0.009;
        this.velY = dx * 0.0008;
        if (!this.raf) this.draw();
      } else if (inside) {
        this.pickHover();
      }
    };
    const up = () => { this.dragging = false; };

    this.canvas.addEventListener('pointerdown', down);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    this.canvas.addEventListener('pointerleave', () => {
      this.pointer = null;
      if (!this.hoverFromList) this.setHover(-1);
    });
  }

  bindList() {
    this.markers.forEach((m) => {
      if (!m.row) return;
      m.row.addEventListener('mouseenter', () => this.setHover(m.i, true));
      m.row.addEventListener('mouseleave', () => this.setHover(-1, true));
      m.row.addEventListener('focusin', () => this.setHover(m.i, true));
      m.row.addEventListener('focusout', () => this.setHover(-1, true));
    });
  }

  pickHover() {
    if (!this.pointer || !this.markerPts) return;
    let found = -1;
    let best = Infinity;
    this.markerPts.forEach((p) => {
      const d = Math.hypot(p.sx - this.pointer.x, p.sy - this.pointer.y);
      if (d < 18 && d < best) { best = d; found = p.i; }
    });
    this.setHover(found);
  }

  setHover(i, fromList = false) {
    this.hoverFromList = fromList && i >= 0;
    if (this.hover === i) return;
    this.hover = i;
    this.markers.forEach((m) => m.row?.classList.toggle('is-active', m.i === i));
    if (!fromList) this.canvas.style.cursor = i >= 0 ? 'pointer' : '';
    if (!this.raf) this.draw();
  }

  /* position of one strand at normalised height u (0 bottom → 1 top) */
  point(u, phase) {
    const angle = u * Math.PI * 2 * this.turns + phase + this.rotY;
    const depth = this.radius * this.depthFactor;
    const x = Math.cos(angle) * this.radius;
    const z = Math.sin(angle);
    const scale = depth / (depth + z * this.radius);
    return {
      sx: this.w / 2 + x * scale,
      sy: this.h / 2 + (u - 0.5) * this.span * scale,
      z,
      scale,
    };
  }

  draw() {
    const ctx = this.ctx;
    if (!ctx || !this.w) return;
    ctx.clearRect(0, 0, this.w, this.h);

    const items = [];

    for (let i = 0; i < this.samples; i++) {
      const u = i / (this.samples - 1);
      const a = this.point(u, 0);
      const b = this.point(u, Math.PI);

      if (i % this.rungEvery === 0) {
        items.push({ kind: 'rung', a, b, z: (a.z + b.z) / 2 });
      }
      if (i > 0) {
        const pu = (i - 1) / (this.samples - 1);
        items.push({ kind: 'seg', from: this.point(pu, 0), to: a, color: this.strandA, z: a.z });
        items.push({ kind: 'seg', from: this.point(pu, Math.PI), to: b, color: this.strandB, z: b.z });
      }
    }

    this.markerPts = this.markers.map((m) => {
      const p = this.point(m.at, 0);
      return { i: m.i, sx: p.sx, sy: p.sy, z: p.z, scale: p.scale };
    });
    this.markerPts.forEach((p) => items.push({ kind: 'marker', p, z: p.z }));

    items.sort((m, n) => m.z - n.z);

    items.forEach((it) => {
      const dim = 0.28 + ((-it.z + 1) / 2) * 0.72;
      if (it.kind === 'seg') {
        ctx.beginPath();
        ctx.moveTo(it.from.sx, it.from.sy);
        ctx.lineTo(it.to.sx, it.to.sy);
        ctx.strokeStyle = this.mix(it.color, dim);
        ctx.lineWidth = 3.4 * it.to.scale;
        ctx.lineCap = 'round';
        ctx.stroke();
      } else if (it.kind === 'rung') {
        ctx.beginPath();
        ctx.moveTo(it.a.sx, it.a.sy);
        ctx.lineTo(it.b.sx, it.b.sy);
        ctx.strokeStyle = this.mix(this.rungColor, dim * 0.34);
        ctx.lineWidth = 1.4 * ((it.a.scale + it.b.scale) / 2);
        ctx.stroke();
      } else {
        const m = this.markers[it.p.i];
        const active = this.hover === it.p.i;
        const r = (active ? 11 : 7.5) * it.p.scale;
        if (active) {
          ctx.beginPath();
          ctx.arc(it.p.sx, it.p.sy, r + 8 * it.p.scale, 0, Math.PI * 2);
          ctx.fillStyle = this.hexA(m.color, 0.18);
          ctx.fill();
        }
        ctx.beginPath();
        ctx.arc(it.p.sx, it.p.sy, r, 0, Math.PI * 2);
        ctx.fillStyle = active ? this.mix(m.color, 1) : this.mix(m.color, Math.min(dim + 0.2, 1));
        ctx.fill();
        if (active) {
          ctx.strokeStyle = this.hexA('#ffffff', 0.9);
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }
      }
    });

    if (this.hover >= 0) {
      const p = this.markerPts.find((q) => q.i === this.hover);
      if (p) this.label(this.markers[this.hover].label, p);
    }
  }

  label(text, p) {
    const ctx = this.ctx;
    ctx.font = '600 12px ui-sans-serif, system-ui, sans-serif';
    const w = ctx.measureText(text).width + 20;
    const h = 26;
    let x = p.sx + 16;
    if (x + w > this.w - 4) x = p.sx - 16 - w;
    const y = Math.max(4, Math.min(this.h - h - 4, p.sy - h / 2));
    ctx.fillStyle = this.rungColor;
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = '#ffffff';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, x + 10, y + h / 2 + 0.5);
  }

  hexA(hex, alpha) {
    const c = hex.replace('#', '');
    const full = c.length === 3 ? c.split('').map((s) => s + s).join('') : c;
    const n = parseInt(full, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }

  start() {
    if (this.raf || REDUCED.matches) { this.draw(); return; }
    const tick = () => {
      if (!this.dragging) {
        this.rotY += this.velY;
        this.velY += (0.0035 - this.velY) * 0.02;
      }
      this.draw();
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  stop() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
  }
}
customElements.define('sn-helix', SnHelix);

/* --------------------------------------------------------------------------
   Scroll reveal
   -------------------------------------------------------------------------- */
(() => {
  if (REDUCED.matches) return;

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-in');
      io.unobserve(entry.target);
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });

  const scan = (root = document) => {
    root.querySelectorAll('.sn-reveal:not(.is-in)').forEach((el) => io.observe(el));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  } else {
    scan();
  }

  // Shopify re-renders sections in the theme editor
  document.addEventListener('shopify:section:load', (e) => scan(e.target));
})();
